import { useState, useMemo, useEffect } from 'react'
import {
  TrendingUp, TrendingDown, DollarSign, Users, Briefcase,
  Megaphone, BarChart2, ArrowUpRight, ArrowDownRight
} from 'lucide-react'
import type { Client, Campaign, TeamMember, Project, Invoice, Expense, RevenueDataPoint } from '../types'
import { getClients } from '../services/clients.service'
import { getCampaigns } from '../services/campaigns.service'
import { getTeamMembers } from '../services/team.service'
import { getProjects } from '../services/projects.service'
import { getInvoices } from '../services/invoices.service'
import { getExpenses } from '../services/expenses.service'
import { useDateRange } from '../context/DateRangeContext'

// ── Helpers ────────────────────────────────────────────────────
const fmt = (n: number) =>
  n >= 100000
    ? `₹${(n / 100000).toFixed(1)}L`
    : n >= 1000
      ? `₹${(n / 1000).toFixed(0)}K`
      : `₹${n}`

/** Build last-12-months revenue data from paid invoices and expenses */
function buildRevenueData(invoices: Invoice[], expenses: Expense[]): RevenueDataPoint[] {
  const months: RevenueDataPoint[] = []
  const now = new Date()
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const year = d.getFullYear()
    const month = d.getMonth() // 0-indexed
    const label = d.toLocaleString('en', { month: 'short' })

    const revenue = invoices
      .filter(inv => {
        if (inv.status !== 'paid' || !inv.paidAt) return false
        const pd = new Date(inv.paidAt)
        return pd.getFullYear() === year && pd.getMonth() === month
      })
      .reduce((s, inv) => s + inv.total, 0)

    const expTotal = expenses
      .filter(exp => {
        if (!exp.date) return false
        const ed = new Date(exp.date)
        return ed.getFullYear() === year && ed.getMonth() === month
      })
      .reduce((s, exp) => s + exp.amount, 0)

    months.push({ month: label, revenue, expenses: expTotal, profit: revenue - expTotal })
  }
  return months
}

function StatCard({ label, value, sub, color, trend, icon: Icon }: {
  label: string; value: string; sub: string; color: string; trend?: 'up' | 'down'; icon: React.ElementType
}) {
  return (
    <div className="rounded-2xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.08)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,198,0,0.1)' }}>
          <Icon size={17} style={{ color }} />
        </div>
        {trend && (
          trend === 'up'
            ? <ArrowUpRight size={16} style={{ color: '#32c864' }} />
            : <ArrowDownRight size={16} style={{ color: '#cc3300' }} />
        )}
      </div>
      <p className="text-2xl font-bold mt-1" style={{ color, fontFamily: 'Syne, sans-serif' }}>{value}</p>
      <p className="text-sm font-medium text-white mt-0.5">{label}</p>
      <p className="text-xs mt-0.5" style={{ color: '#555' }}>{sub}</p>
    </div>
  )
}

// ── Mini bar chart ─────────────────────────────────────────────
function BarMini({ data, colorKey }: { data: { label: string; value: number }[]; colorKey: string }) {
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map(d => (
        <div key={d.label} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full rounded-t-sm transition-all"
            style={{ height: `${(d.value / max) * 100}%`, background: colorKey, minHeight: 2, opacity: 0.85 }} />
          <span style={{ fontSize: 9, color: '#555', writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 30 }}>{d.label}</span>
        </div>
      ))}
    </div>
  )
}

// ── Revenue Section ────────────────────────────────────────────
function RevenueSection({ data }: { data: RevenueDataPoint[] }) {
  const last = data[data.length - 1] ?? { revenue: 0, expenses: 0, profit: 0, month: '' }
  const prev = data[data.length - 2] ?? last
  const revGrowth = prev.revenue > 0 ? (((last.revenue - prev.revenue) / prev.revenue) * 100).toFixed(1) : '0.0'
  const profitMargin = last.revenue > 0 ? ((last.profit / last.revenue) * 100).toFixed(0) : '0'

  const maxVal = Math.max(...data.map(d => Math.max(d.revenue, d.expenses)), 1)

  return (
    <div className="rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Revenue vs Expenses</h3>
          <p className="text-xs mt-0.5" style={{ color: '#555' }}>Last 12 months</p>
        </div>
        <div className="flex gap-4 text-xs">
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#ffc600' }} /><span style={{ color: '#a0a0a0' }}>Revenue</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#cc3300' }} /><span style={{ color: '#a0a0a0' }}>Expenses</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#32c864' }} /><span style={{ color: '#a0a0a0' }}>Profit</span></div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex items-end gap-2" style={{ height: 160 }}>
        {data.map(d => (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end">
            <div className="w-full flex gap-0.5 items-end" style={{ height: '90%' }}>
              <div className="flex-1 rounded-t-sm" title={`Revenue: ${fmt(d.revenue)}`}
                style={{ height: `${(d.revenue / maxVal) * 100}%`, background: '#ffc600', opacity: 0.9, minHeight: 2 }} />
              <div className="flex-1 rounded-t-sm" title={`Expenses: ${fmt(d.expenses)}`}
                style={{ height: `${(d.expenses / maxVal) * 100}%`, background: '#cc3300', opacity: 0.7, minHeight: 2 }} />
              <div className="flex-1 rounded-t-sm" title={`Profit: ${fmt(d.profit)}`}
                style={{ height: `${(d.profit / maxVal) * 100}%`, background: '#32c864', opacity: 0.8, minHeight: 2 }} />
            </div>
            <span style={{ fontSize: 9, color: '#555' }}>{d.month}</span>
          </div>
        ))}
      </div>

      {/* Bottom stats */}
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t" style={{ borderColor: 'rgba(255,198,0,0.06)' }}>
        <div>
          <p className="text-xs" style={{ color: '#555' }}>This Month Revenue</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: '#ffc600', fontFamily: 'Syne, sans-serif' }}>{fmt(last.revenue)}</p>
          <p className="text-xs" style={{ color: Number(revGrowth) >= 0 ? '#32c864' : '#cc3300' }}>
            {Number(revGrowth) >= 0 ? '+' : ''}{revGrowth}% vs last month
          </p>
        </div>
        <div>
          <p className="text-xs" style={{ color: '#555' }}>This Month Expenses</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: '#cc3300', fontFamily: 'Syne, sans-serif' }}>{fmt(last.expenses)}</p>
          <p className="text-xs" style={{ color: '#555' }}>operational costs</p>
        </div>
        <div>
          <p className="text-xs" style={{ color: '#555' }}>Profit Margin</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: '#32c864', fontFamily: 'Syne, sans-serif' }}>{profitMargin}%</p>
          <p className="text-xs" style={{ color: '#555' }}>{fmt(last.profit)} net profit</p>
        </div>
      </div>
    </div>
  )
}

// ── Client MRR Breakdown ───────────────────────────────────────
function ClientMRRSection({ clients }: { clients: Client[] }) {
  const active = [...clients]
    .filter(c => c.status === 'active')
    .sort((a, b) => b.mrr - a.mrr)
  const totalMRR = active.reduce((s, c) => s + c.mrr, 0)

  return (
    <div className="rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
      <h3 className="font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Client MRR Breakdown</h3>
      <p className="text-xs mb-5" style={{ color: '#555' }}>Active clients ranked by monthly recurring revenue</p>
      <div className="space-y-3">
        {active.slice(0, 8).map(c => {
          const pct = totalMRR > 0 ? (c.mrr / totalMRR) * 100 : 0
          return (
            <div key={c.id}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-white truncate max-w-[160px]">{c.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs" style={{ color: '#555' }}>{pct.toFixed(1)}%</span>
                  <span className="text-sm font-bold" style={{ color: '#ffc600' }}>{fmt(c.mrr)}</span>
                </div>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
                <div className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #ffc600, #e6a000)' }} />
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: 'rgba(255,198,0,0.06)' }}>
        <span className="text-xs" style={{ color: '#555' }}>Total Active MRR</span>
        <span className="text-lg font-bold" style={{ color: '#ffc600', fontFamily: 'Syne, sans-serif' }}>{fmt(totalMRR)}</span>
      </div>
    </div>
  )
}

// ── Campaign Performance ───────────────────────────────────────
function CampaignSection({ campaigns }: { campaigns: Campaign[] }) {
  const active = campaigns.filter(c => c.status === 'active')

  const typeData = useMemo(() => {
    const map: Record<string, { spend: number; revenue: number; count: number }> = {}
    for (const c of active) {
      if (!map[c.type]) map[c.type] = { spend: 0, revenue: 0, count: 0 }
      map[c.type].spend += c.spend
      map[c.type].revenue += c.revenue
      map[c.type].count++
    }
    return Object.entries(map).map(([type, d]) => ({
      type, ...d, roas: d.spend > 0 ? d.revenue / d.spend : 0,
    })).sort((a, b) => b.revenue - a.revenue)
  }, [active])

  return (
    <div className="rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
      <h3 className="font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Campaign Performance by Type</h3>
      <p className="text-xs mb-5" style={{ color: '#555' }}>Active campaigns this month</p>
      <div className="space-y-3">
        {typeData.map(t => (
          <div key={t.type} className="flex items-center gap-4 p-3 rounded-xl" style={{ background: '#1a1a1a' }}>
            <div className="w-24 flex-shrink-0">
              <p className="text-sm font-semibold text-white">{t.type}</p>
              <p className="text-xs" style={{ color: '#555' }}>{t.count} campaign{t.count !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs font-bold" style={{ color: '#cc3300' }}>{fmt(t.spend)}</p>
                <p style={{ fontSize: 10, color: '#555' }}>Spend</p>
              </div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#32c864' }}>{fmt(t.revenue)}</p>
                <p style={{ fontSize: 10, color: '#555' }}>Revenue</p>
              </div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#ffc600' }}>{t.roas.toFixed(1)}x</p>
                <p style={{ fontSize: 10, color: '#555' }}>ROAS</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Team Utilization ───────────────────────────────────────────
function TeamSection({ members }: { members: TeamMember[] }) {
  const sorted = [...members].sort((a, b) => b.utilization - a.utilization)

  return (
    <div className="rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
      <h3 className="font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Team Utilization</h3>
      <p className="text-xs mb-5" style={{ color: '#555' }}>Workload distribution this month</p>
      <div className="space-y-4">
        {sorted.map(m => {
          const color = m.utilization >= 90 ? '#cc3300' : m.utilization >= 75 ? '#ffc600' : '#32c864'
          return (
            <div key={m.id}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: '#ffc600', color: '#000' }}>{m.name[0]}</div>
                  <div>
                    <span className="text-sm font-medium text-white">{m.name}</span>
                    <span className="text-xs ml-2" style={{ color: '#555' }}>{m.role}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold" style={{ color }}>{m.utilization}%</span>
                  <span className="text-xs ml-2" style={{ color: '#555' }}>{m.hoursThisMonth}h</span>
                </div>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${m.utilization}%`, background: color }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Project Status Breakdown ───────────────────────────────────
function ProjectStatusSection({ projects, invoices }: { projects: Project[]; invoices: Invoice[] }) {
  const byStatus = {
    planning:    projects.filter(p => p.status === 'planning').length,
    'in-progress': projects.filter(p => p.status === 'in-progress').length,
    review:      projects.filter(p => p.status === 'review').length,
    done:        projects.filter(p => p.status === 'done').length,
  }
  const total = projects.length

  const items = [
    { label: 'Planning',    val: byStatus.planning,        color: '#555' },
    { label: 'In Progress', val: byStatus['in-progress'],  color: '#ffc600' },
    { label: 'In Review',   val: byStatus.review,          color: '#7c7cff' },
    { label: 'Done',        val: byStatus.done,            color: '#32c864' },
  ]

  // Invoice stats
  const invStats = {
    paid:    invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0),
    sent:    invoices.filter(i => i.status === 'sent').reduce((s, i) => s + i.total, 0),
    overdue: invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + i.total, 0),
    draft:   invoices.filter(i => i.status === 'draft').reduce((s, i) => s + i.total, 0),
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {/* Projects */}
      <div className="rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
        <h3 className="font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Project Status</h3>
        <p className="text-xs mb-5" style={{ color: '#555' }}>{total} total projects</p>
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                <span className="text-sm text-white">{item.label}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 h-2 rounded-full overflow-hidden" style={{ background: '#1a1a1a' }}>
                  <div className="h-full rounded-full" style={{ width: `${total > 0 ? (item.val / total) * 100 : 0}%`, background: item.color }} />
                </div>
                <span className="text-sm font-bold w-4 text-right" style={{ color: item.color }}>{item.val}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invoices */}
      <div className="rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
        <h3 className="font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Invoice Summary</h3>
        <p className="text-xs mb-5" style={{ color: '#555' }}>{invoices.length} total invoices</p>
        <div className="space-y-3">
          {[
            { label: 'Paid',    val: invStats.paid,    color: '#32c864' },
            { label: 'Sent',    val: invStats.sent,    color: '#ffc600' },
            { label: 'Overdue', val: invStats.overdue, color: '#cc3300' },
            { label: 'Draft',   val: invStats.draft,   color: '#555' },
          ].map(item => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                <span className="text-sm text-white">{item.label}</span>
              </div>
              <span className="text-sm font-bold" style={{ color: item.color }}>{fmt(item.val)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────
export default function Reports() {
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month')
  const { inRange } = useDateRange()

  const [clients, setClients] = useState<Client[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])

  useEffect(() => {
    getClients().then(setClients).catch(console.error)
    getCampaigns().then(setCampaigns).catch(console.error)
    getTeamMembers().then(setTeamMembers).catch(console.error)
    getProjects().then(setProjects).catch(console.error)
    getInvoices().then(setInvoices).catch(console.error)
    getExpenses().then(setExpenses).catch(console.error)
  }, [])

  // Compute revenue data from real invoices + expenses (last 12 months)
  const allRevenueData = useMemo(() => buildRevenueData(invoices, expenses), [invoices, expenses])

  // Map month label to a mid-month ISO date for range comparison
  const revenueData = useMemo(() => {
    const now = new Date()
    const filtered = allRevenueData.filter(d => {
      // find the matching month index in our 12-month window
      const idx = allRevenueData.indexOf(d)
      const monthsAgo = allRevenueData.length - 1 - idx
      const date = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 15)
      return inRange(date.toISOString().split('T')[0])
    })
    return filtered.length > 0 ? filtered : allRevenueData
  }, [allRevenueData, inRange])

  const totalMRR = clients.filter(c => c.status === 'active').reduce((s, c) => s + c.mrr, 0)
  const activeClients = clients.filter(c => c.status === 'active').length
  const activeProjects = projects.filter(p => p.status === 'in-progress' || p.status === 'review').length
  const totalRoas = (() => {
    const active = campaigns.filter(c => c.status === 'active')
    const totalSpend = active.reduce((s, c) => s + c.spend, 0)
    const totalRev   = active.reduce((s, c) => s + c.revenue, 0)
    return totalSpend > 0 ? totalRev / totalSpend : 0
  })()

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Reports</h1>
          <p className="text-sm mt-0.5" style={{ color: '#555' }}>Business analytics and performance overview</p>
        </div>
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.1)' }}>
          {(['month', 'quarter', 'year'] as const).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold capitalize transition-all"
              style={period === p ? { background: '#ffc600', color: '#000' } : { color: '#a0a0a0' }}
              onMouseEnter={e => { if (period !== p) e.currentTarget.style.color = '#fff' }}
              onMouseLeave={e => { if (period !== p) e.currentTarget.style.color = '#a0a0a0' }}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Top KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Monthly MRR" value={fmt(totalMRR)} sub="+12.3% vs last month" color="#ffc600" trend="up" icon={DollarSign} />
        <StatCard label="Active Clients" value={String(activeClients)} sub="2 new this month" color="#32c864" trend="up" icon={Users} />
        <StatCard label="Active Projects" value={String(activeProjects)} sub="3 completed this month" color="#7c7cff" icon={Briefcase} />
        <StatCard label="Avg. ROAS" value={`${totalRoas.toFixed(1)}x`} sub="across active campaigns" color="#ff8c00" trend="up" icon={Megaphone} />
      </div>

      {/* Revenue chart */}
      <RevenueSection data={revenueData} />

      {/* Two column */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ClientMRRSection clients={clients} />
        <CampaignSection campaigns={campaigns} />
      </div>

      {/* Team + Projects */}
      <TeamSection members={teamMembers} />
      <ProjectStatusSection projects={projects} invoices={invoices} />
    </div>
  )
}
